import { ArtworkError } from "./errors"

const TURNSTILE_SCRIPT_ID = "craft-force-turnstile-script"
const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

type TurnstileRenderParameters = {
  action?: string
  appearance?: "always" | "execute" | "interaction-only"
  callback?: (token: string) => void
  "error-callback"?: () => void
  execution?: "execute" | "render"
  "expired-callback"?: () => void
  sitekey: string
  size?: "flexible" | "invisible" | "normal"
  theme?: "auto" | "dark" | "light"
  "timeout-callback"?: () => void
}

type TurnstileApi = {
  execute: (widgetId: string) => void
  remove: (widgetId: string) => void
  render: (
    container: HTMLElement | string,
    parameters: TurnstileRenderParameters,
  ) => string
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let turnstileScriptPromise: Promise<TurnstileApi> | null = null

type TurnstilePublicEnv = {
  PUBLIC_TURNSTILE_SITE_KEY?: string
}

export function getTurnstileSiteKey(
  env: TurnstilePublicEnv = import.meta.env,
): string | null {
  const siteKey = env.PUBLIC_TURNSTILE_SITE_KEY?.trim()
  return siteKey ? siteKey : null
}

export async function requestTurnstileToken(
  action = "artwork_upload",
  env: TurnstilePublicEnv = import.meta.env,
): Promise<string | null> {
  const siteKey = getTurnstileSiteKey(env)
  if (!siteKey) return null

  const api = await loadTurnstileApi()

  return new Promise<string>((resolve, reject) => {
    const container = document.createElement("div")
    container.hidden = true
    document.body.appendChild(container)

    let widgetId = ""
    let settled = false

    const cleanup = () => {
      if (widgetId) {
        api.remove(widgetId)
      }
      container.remove()
    }

    const settle = (next: () => void) => {
      if (settled) return
      settled = true
      cleanup()
      next()
    }

    widgetId = api.render(container, {
      action,
      appearance: "execute",
      callback(token) {
        settle(() => resolve(token))
      },
      "error-callback"() {
        settle(() => reject(new ArtworkError("ARTWORK_TURNSTILE_FAILED")))
      },
      execution: "execute",
      "expired-callback"() {
        settle(() => reject(new ArtworkError("ARTWORK_TURNSTILE_REQUIRED")))
      },
      sitekey: siteKey,
      size: "invisible",
      "timeout-callback"() {
        settle(() => reject(new ArtworkError("ARTWORK_TURNSTILE_FAILED")))
      },
    })

    try {
      api.execute(widgetId)
    } catch {
      settle(() => reject(new ArtworkError("ARTWORK_TURNSTILE_UNAVAILABLE")))
    }
  })
}

function loadTurnstileApi(): Promise<TurnstileApi> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new ArtworkError("ARTWORK_TURNSTILE_UNAVAILABLE"))
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise
  }

  turnstileScriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID)
    if (existingScript instanceof HTMLScriptElement) {
      existingScript.addEventListener("load", handleLoad, { once: true })
      existingScript.addEventListener("error", handleError, { once: true })
      return
    }

    const script = document.createElement("script")
    script.id = TURNSTILE_SCRIPT_ID
    script.async = true
    script.defer = true
    script.src = TURNSTILE_SCRIPT_URL
    script.addEventListener("load", handleLoad, { once: true })
    script.addEventListener("error", handleError, { once: true })
    document.head.appendChild(script)

    function handleLoad() {
      if (window.turnstile) {
        resolve(window.turnstile)
        return
      }
      turnstileScriptPromise = null
      reject(new ArtworkError("ARTWORK_TURNSTILE_UNAVAILABLE"))
    }

    function handleError() {
      turnstileScriptPromise = null
      reject(new ArtworkError("ARTWORK_TURNSTILE_UNAVAILABLE"))
    }
  })

  return turnstileScriptPromise
}
